import React, { useState } from "react";
import { NavLink, useNavigate, Navigate, useLocation } from "react-router-dom";
import { Icon } from "@/components/kit";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { api, isSuperadmin } from "@/api";
import { useI18n } from "@/i18n";
import { cn } from "@/lib/utils";

export function ProtectedRoute({ children }) {
  const location = useLocation();
  if (!api.hasToken()) return <Navigate to="/admin" replace state={{ from: location }} />;
  return children;
}

const NavItems = ({ links, onClick }) => (
  <>
    {links.map((l) => (
      <NavLink
        key={l.to}
        to={l.to}
        onClick={onClick}
        data-testid={`nav-${l.icon}`}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[14px] font-medium transition-colors",
            isActive ? "bg-brand text-white" : "text-ink-2 hover:bg-paper-3"
          )
        }
      >
        <Icon name={l.icon} size={18} /> {l.label}
      </NavLink>
    ))}
  </>
);

export function AdminShell({ children }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const logout = () => {
    api.adminLogout();
    navigate("/admin", { replace: true });
  };

  const links = [
    { to: "/admin/dashboard", icon: "inbox", label: t("dash_requests") },
    { to: "/admin/crm", icon: "hard-hat", label: t("dash_crm") },
    ...(isSuperadmin() ? [
      { to: "/admin/users", icon: "users", label: t("dash_users") },
      { to: "/admin/subscriptions", icon: "credit-card", label: t("dash_subs") },
    ] : []),
    { to: "/admin/pricing", icon: "sliders", label: t("dash_pricing") },
    { to: "/admin/settings", icon: "briefcase", label: t("dash_settings") },
  ];

  return (
    <div className="min-h-screen bg-paper lg:flex">
      {/* Sidebar (desktop) */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-line bg-white p-4 lg:flex">
        <img src="/logo.png" alt="Déco Ligot" className="mb-6 mt-2 h-9 self-start object-contain" />
        <nav className="flex flex-1 flex-col gap-1"><NavItems links={links} /></nav>
        <div className="flex flex-col gap-1 border-t border-divider pt-3">
          <button onClick={() => navigate("/")} data-testid="admin-back-home" className="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[14px] font-medium text-ink-2 hover:bg-paper-3">
            <Icon name="home" size={18} /> {t("nav_back_home")}
          </button>
          <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[14px] font-medium text-err hover:bg-paper-3">
            <Icon name="log-out" size={18} /> {t("admin_logout")}
          </button>
          <div className="px-1 pt-2"><LanguageSwitcher /></div>
        </div>
      </aside>

      {/* Top bar (mobile) */}
      <div className="sticky top-0 z-40 flex items-center justify-between border-b border-line bg-white px-4 py-3 lg:hidden">
        <img src="/logo.png" alt="Déco Ligot" className="h-8 object-contain" />
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <button onClick={() => setOpen((o) => !o)} data-testid="admin-menu-toggle" className="flex h-10 w-10 items-center justify-center rounded-full border border-line">
            <Icon name={open ? "x" : "menu"} size={20} className="text-ink" />
          </button>
        </div>
      </div>
      {open && (
        <div className="border-b border-line bg-white p-3 lg:hidden">
          <nav className="flex flex-col gap-1"><NavItems links={links} onClick={() => setOpen(false)} /></nav>
          <div className="mt-2 flex flex-col gap-1 border-t border-divider pt-2">
            <button onClick={() => navigate("/")} className="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[14px] font-medium text-ink-2">
              <Icon name="home" size={18} /> {t("nav_back_home")}
            </button>
            <button onClick={logout} className="flex items-center gap-3 rounded-xl px-3.5 py-2.5 text-[14px] font-medium text-err">
              <Icon name="log-out" size={18} /> {t("admin_logout")}
            </button>
          </div>
        </div>
      )}

      <main className="min-w-0 flex-1">{children}</main>
    </div>
  );
}
